import React, { useRef, useState, useEffect } from "react";
import useDashboard from "../../../hooks/useDashboard";
import useAuth from "../../../hooks/useAuth";
import styled from "@emotion/styled";
import { Box, Card, Typography, Grid } from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { RefreshCcw, TrendingUp } from "react-feather";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const GlassCard = styled(Card)`
  background: ${(props) =>
    props.theme.palette.mode === "dark"
      ? props.theme.palette.background.paper
      : "rgba(255, 255, 255, 0.9)"};
  backdrop-filter: blur(10px);
  border-radius: 20px;
  box-shadow: ${(props) =>
    props.theme.palette.mode === "dark"
      ? "0 10px 40px -10px rgba(0, 0, 0, 0.4)"
      : "0 10px 40px -10px rgba(0, 0, 0, 0.1)"};
  border: ${(props) =>
    props.theme.palette.mode === "dark"
      ? "1px solid rgba(255, 255, 255, 0.08)"
      : "1px solid rgba(255, 255, 255, 0.3)"};
  overflow: hidden;
`;

const StatBadge = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 30px;
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  font-size: 10px;
  font-weight: 700;
`;
const HeaderSection = ({ totalRenewals }) => (
  <Box sx={{ p: 2, pb: 0 }}>
    <Grid container justifyContent="space-between" alignItems="center">
      <Grid item>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.25 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 800, color: "text.primary", fontSize: "13px" }}
          >
            Last One Week Renewal
          </Typography>
          <StatBadge>
            <RefreshCcw size={10} />
            Active
          </StatBadge>
        </Box>
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", fontWeight: 500, fontSize: "11px" }}
        >
          Daily Renewal Statistics
        </Typography>
      </Grid>
      <Grid item>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TrendingUp size={14} color="#3b82f6" />
          <Typography variant="caption" sx={{ fontWeight: 700, color: "#3b82f6" }}>
            {totalRenewals > 0 ? "+8.2%" : "0%"}
          </Typography>
        </Box>
      </Grid>
    </Grid>

    <Box sx={{ mt: 1.5, display: "flex", gap: 4 }}>
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: "9px",
          }}
        >
          Total Renewals
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary", mt: 0 }}>
          {totalRenewals}
        </Typography>
      </Box>
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            fontSize: "9px",
          }}
        >
          Success Rate
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 800, color: "text.primary", mt: 0 }}>
          {totalRenewals > 0 ? "98.5%" : "0%"}
        </Typography>
      </Box>
    </Box>
  </Box>
);

const RenewalChart = () => {
  const chartRef = useRef(null);
  const { user } = useAuth();
  const { getRenewalChartData, renewalChartData, isRenewalLoading } = useDashboard();

  const fetchData = () => {
    const filters = [];
    if (user && user.businessType !== "SUPER_ADMIN" && user.tenantId) {
      filters.push({
        fieldName: "tenantId",
        fieldValue: user.tenantId,
        op: "eq",
        dataType: "long",
        condition: "and",
        isJoin: false,
      });
    }

    // Last 7 days filter
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 7);

    filters.push({
      fieldName: "createdAt",
      fieldValue: startDate.toISOString().split("T")[0],
      op: "ge",
      dataType: "date",
      condition: "and",
    });

    filters.push({
      fieldName: "createdAt",
      fieldValue: endDate.toISOString().split("T")[0],
      op: "le",
      dataType: "date",
      condition: "and",
    });

    getRenewalChartData({
      filters,
      pagination: { pageNumber: 1, pageSize: 100 },
      sortFields: [{ fieldName: "createdAt", sortOrder: "ASC" }],
    });
  };

  useEffect(() => {
    fetchData();
  }, [user]);

  const labels =
    (renewalChartData?.length || 0) > 0
      ? renewalChartData.map((d) => {
          const date = new Date(d.createdAt);
          return date.toLocaleDateString("en-US", { weekday: "short" });
        })
      : Array.from({ length: 7 }, (_, i) => {
          const d = new Date();
          d.setDate(d.getDate() - (6 - i));
          return d.toLocaleDateString("en-US", { weekday: "short" });
        });

  const dataPoints =
    (renewalChartData?.length || 0) > 0
      ? renewalChartData.map((d) => d.count || 0)
      : Array(7).fill(0);

  const totalRenewals = dataPoints.reduce((a, b) => a + b, 0);

  const chartData = {
    labels,
    datasets: [
      {
        label: "Renewals",
        data: dataPoints,
        borderColor: "#3b82f6",
        borderWidth: 2,
        pointRadius: 3,
        pointBackgroundColor: "#3b82f6",
        pointBorderColor: "#fff",
        pointBorderWidth: 1.5,
        tension: 0.4,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 1200,
      easing: "easeInOutQuart",
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#1e293b",
        padding: 8,
        borderRadius: 6,
        titleFont: { size: 12, weight: "bold" },
        bodyFont: { size: 11 },
        displayColors: false,
      },
    },
    scales: {
      x: {
        display: true,
        grid: { display: false },
        ticks: {
          color: "#94a3b8",
          font: { size: 9, weight: 600 },
        },
      },
      y: {
        display: true,
        beginAtZero: true,
        grid: {
          color: "rgba(0, 0, 0, 0.03)",
          drawBorder: false,
        },
        ticks: {
          color: "#94a3b8",
          font: { size: 9 },
          maxTicksLimit: 4,
        },
      },
    },
    interaction: {
      intersect: false,
      mode: "index",
    },
  };

  return (
    <Grid item xs={12} md={6}>
      <GlassCard>
        <HeaderSection totalRenewals={totalRenewals} />
        <Box sx={{ height: 140, mt: 0.5, position: "relative" }}>
          {isRenewalLoading ? (
            <Box
              sx={{
                display: "flex",
                height: "100%",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                Loading Data...
              </Typography>
            </Box>
          ) : (
            <Line data={chartData} options={options} ref={chartRef} />
          )}
        </Box>
      </GlassCard>
    </Grid>
  );
};

export default RenewalChart;
